import { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../components/shared/DatePicker/DatePicker';
import EmailPreview from '../../components/shared/EmailPreview/EmailPreview';
import Field from '../../components/shared/Field/Field';
import appState from '../../state/AppStateContainer';
import { EvaluationLimits } from '../../util/evaluationContract';
import { fromIsoDay, toIsoDay } from '../../util/bidRequestReminder';
import RecipientField from '../app-evaluation-start/RecipientField';
import { isOwnRecipientOption } from '../app-evaluation-start/evaluationStart';
import { WizardAction, WizardState, areaIdFor, defaultMessage } from './wizardState';
import styles from './app-evaluation-wizard.module.scss';

type Props = {
  state: WizardState;
  dispatch: Dispatch<WizardAction>;
  today: string;
};

const RecipientsStep = ({ state, dispatch, today }: Props) => {
  const [t] = useTranslation('evaluations');
  const message = state.message ?? defaultMessage(t, state);
  const tomorrow = fromIsoDay(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const previewed = state.recipients.find(recipient => !isOwnRecipientOption(recipient));
  const previewName = previewed?.name || previewed?.label;
  const supplierCount = state.suppliers.length;
  const previewSupplier = state.suppliers[0];
  const project = state.contextChoice === 'project' ? state.project : null;
  const previewAreaId = previewSupplier ? areaIdFor(project, previewSupplier) : null;
  const previewArea = project?.areas.find(area => area.areaId === previewAreaId)?.name;

  return (
    <div className={styles.fields}>
      <RecipientField
        noEmailFor={{
          userId: appState.getUserId(),
          email: appState.getUserEmail(),
          name: appState.getUserName(),
        }}
        value={state.recipients}
        onChange={recipients => dispatch({ type: 'setRecipients', recipients })}
      />
      <div className={styles.fieldStack}>
        <DatePicker
          label={t('quick.dueDate', 'Sista svarsdag')}
          selected={fromIsoDay(state.dueDay)}
          minDate={tomorrow}
          onChange={(next: Date | null) =>
            next && dispatch({ type: 'setDueDay', dueDay: toIsoDay(next) })
          }
        />
        <p className={styles.hint}>
          {t(
            'wizard.recipients.dueHelp',
            'Svar tas emot hela dagen. Svar som kommer senare markeras som sena.',
          )}
        </p>
      </div>
      <div className={styles.fieldStack}>
        <Field
          multiline
          label={t('wizard.recipients.message', 'Meddelande till mottagarna')}
          value={message}
          onChange={text =>
            dispatch({
              type: 'setMessage',
              message: text.slice(0, EvaluationLimits.MessageMaxLength),
            })
          }
        />
        <p className={styles.hint}>
          {t(
            'wizard.recipients.messageHelp',
            'Visas i mejlet och överst i formuläret. Bara text, inga länkar eller formatering.',
          )}
        </p>
      </div>
      <div className={styles.fieldStack}>
        <p className={styles.sectionLabel}>
          {previewName
            ? t('wizard.recipients.previewFor', 'Förhandsvisning av mejlet för {{name}}', {
                name: previewName,
              })
            : t('wizard.recipients.preview', 'Förhandsvisning av mejlet')}
        </p>
        {supplierCount > 1 && (
          <p className={styles.hint}>
            {t('wizard.recipients.previewNote', {
              count: supplierCount,
              defaultValue_one: 'Varje mottagare får {{count}} mejl.',
              defaultValue_other: 'Varje mottagare får {{count}} mejl, ett per leverantör.',
            })}
          </p>
        )}
        <EmailPreview
          type="evaluation"
          params={{
            recipientName: previewName,
            supplierName: previewSupplier?.name,
            projectName:
              state.contextChoice === 'historical'
                ? state.historicalName.trim() || undefined
                : state.project?.name,
            projectNumber:
              state.contextChoice === 'project'
                ? state.project?.number ?? undefined
                : state.historicalNumber.trim() || undefined,
            dueDate: state.dueDay,
            areaName: previewArea,
          }}
        >
          <p className={styles.previewMessage}>{message}</p>
        </EmailPreview>
      </div>
    </div>
  );
};

export default RecipientsStep;
